<template>
  <div
    v-if="isChargingpointSelected"
    class="ActionSidebar"
    :class="{ 'open': open }"
  >
    <div class="Tabs">
      <div class="Tabs__Container">
        <div
          v-for="tab in tabs"
          :id="tab.id"
          :key="tab.id"
          v-b-tooltip.hover.left="$t(tab.tooltip)"
          class="Tab"
          :class="{ 'active': activeTab.value === tab.value }"
          @click="setActiveTab(tab.value)"
        >
          <i :class="tab.icon" />
        </div>
      </div>
      <div
        v-if="isChargingpointSelected"
        class="Tab ActionSidebar__Collapse"
        @click="toggle"
      >
        <div
          class="ActionSidebar__Collapse__Icon"
          :class="{ rotated: open }"
        >
          <i class="fas fa-chevron-left" />
        </div>
      </div>
    </div>

    <div
      class="ActiveTab__Close hasScrollbar"
      @click="close"
    >
      <i class="fas fa-times" />
    </div>

    <component :is="activeTab.component" />
  </div>
</template>

<script>
import InfoTab from '@/components/map/sidebar/Tabs/InfoTab'
import MergeTab from '@/components/map/sidebar/Tabs/MergeTab'
import MonitoringTab from '@/components/map/sidebar/Tabs/MonitoringTab'
import OpmerkingenTab from '@/components/map/sidebar/Tabs/OpmerkingenTab'
import ParticipationTab from '@/components/map/sidebar/Tabs/ParticipationTab'
import ProcessInfoTab from '@/components/map/sidebar/Tabs/ProcessInfoTab'
import ImageGallery from './Tabs/ImageGallery.vue'
import { mapActions, mapGetters } from 'vuex'
import { EventBus } from '@/services/eventbus'

export default {
  name: 'ActionSidebar',
  components: {
    OpmerkingenTab,
    MonitoringTab,
    ParticipationTab,
    MergeTab,
    InfoTab,
    ProcessInfoTab,
    ImageGallery,
  },
  data() {
    return {
      open: true,
    }
  },
  computed: {
    ...mapGetters('planmode', ['isChargingpointSelected']),
    ...mapGetters('app', ['getSelectedActionSidebarTab']),
    tabs () {
      const tabs = [
        {
          id: 'info-tab-button',
          value: 'info',
          icon: 'far fa-fw fa-info-circle',
          visible: true,
          component: 'InfoTab',
          tooltip: 'components.sidebar.Tabs.InfoTab',
        },
        {
          id: 'image-carousel-button',
          value: 'images',
          icon: 'far fa-fw fa-image',
          visible: true,
          component: 'ImageGallery',
          tooltip: 'components.sidebar.Tabs.Images.title',
        },
        {
          id: 'merge-tab-button',
          value: 'merge',
          icon: 'far fa-fw fa-merge',
          visible: true,
          component: 'MergeTab',
          tooltip: 'components.sidebar.MergeTab.title',
        },
        {
          id: 'comments-tab-button',
          value: 'comments',
          icon: 'far fa-fw fa-comment-dots',
          visible: true,
          component: 'OpmerkingenTab',
          tooltip: 'components.sidebar.Tabs.CommentsTab.title',
        },
        {
          id: 'reacties-tab-button',
          value: 'participation',
          icon: 'far fa-fw fa-users',
          visible: true,
          component: 'ParticipationTab',
          tooltip: 'components.sidebar.Tabs.ParticipationTab',
        },
        {
          id: 'monitoring-tab-button',
          value: 'monitoring',
          icon: 'far fa-fw fa-chart-line',
          visible: true,
          component: 'MonitoringTab',
          tooltip: 'components.sidebar.Tabs.MonitoringTab.title',
        },
        {
          id: 'process-info-tab-button',
          value: 'process-info',
          icon: 'far fa-fw fa-arrow-progress',
          visible: true,
          component: 'ProcessInfoTab',
          tooltip: 'components.sidebar.Tabs.ProcessInfoTab.title',
        },
      ]

      return tabs.filter(({ visible }) => visible)
    },
    activeTab() {
      return this.tabs.find(tab => tab.value === this.getSelectedActionSidebarTab)
    },
  },
  watch: {
    isChargingpointSelected() {
      this.toggle()
    },
  },
  methods: {
    ...mapActions('app', ['selectTab']),
    toggle() {
      this.open = !this.open
    },
    close() {
      this.setActiveTab(null)
      /* send signal to close popup and also deselect charging point, which closes the sidebar;
         by that closing is handled in one place (ChargerDetailsPopup & ChargerEditorPopup) */
      EventBus.$emit('deselect-chargingpoint')
    },
    setActiveTab(tabName) {
      this.selectTab({ tabName })

      if (tabName !== null && this.open === false) {
        this.toggle()
      }
    },
  },
}
</script>

<style lang="scss">
$bar-width: 40px;
$container-width: 400px;
$hidden-position: $container-width;
$bar-position: $container-width - $bar-width; // only the tabs are visible

.ActionSidebar {
  position: relative;
  flex: 1 1 auto;
  width: $container-width;
  margin-bottom: 4px;
  background: white;
  display: flex;
  min-height: 0; // fix so that sidebar content doesn't push TileData out of flexbox container
  transition: margin-right .25s ease-out;
  margin-right: - $bar-position; // needs to be margin-right, since transform creates a new bounding box which encapsulates the lightbox
  box-shadow: 0 0 5px -2px;

  &.open {
    margin-right: 0;
  }

  &__Collapse {
    &__Icon {
      transform: rotate(0);
      transition: .25s transform ease-out;

      &.rotated {
        transform: rotate(180deg);
      }
    }
  }

  .Tabs {
    width: $bar-width;
    display: flex;
    flex-direction: column;
    padding-top: 0rem;
    border-right: 1px solid var(--border-color);
    z-index: 1;
    justify-content: space-between;

    .Tabs__Container {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    .Tab {
      display: flex;
      cursor: pointer;
      font-size: 1.1rem;
      padding: 1rem 0;
      width: 100%;
      justify-content: center;
      align-items: center;

      &:hover {
        background-color: var(--body-bg);
        border-left: 2px solid var(--primary);
        border-right: 2px solid var(--body-bg);
      }

      &.active {
        background-color: var(--body-bg);
        color: var(--primary);
        border-left: 2px solid var(--primary);
        border-right: 2px solid var(--body-bg);
      }
    }
  }

  .ActiveTab {
    flex: 1 1 auto;
    z-index: 2;
    background: white;
    overflow-y: auto;

    &__Header {
      padding: 1rem 0.75rem;
      position: relative;
      border-bottom: 1px solid var(--border-color);

      h4 {
        font-size: 1.3rem;
        margin-top: 1rem;
        font-weight: 400;
      }
    }

    &__Close {
      position: absolute;
      z-index: 3;
      top: 1rem;
      right: 1rem;
      cursor: pointer;
      font-size: 1.25rem;

      &.hasScrollbar {
        right: 28px;
      }
    }

    &__Content {
      padding: 1rem 0.75rem 4rem;
      overflow-x: hidden;
    }
  }
}

.sidebar-enter {
  margin-right: - $hidden-position !important;
}
.sidebar-enter-active,
.sidebar-leave-active {
  transition: margin-right .25s ease-out;
}
.sidebar-leave-to {
  margin-right: - $hidden-position !important;
}
</style>
